import React, { createContext, useContext, ReactNode } from 'react';
import Swal from 'sweetalert2';
import toast, { Toaster } from 'react-hot-toast';
import { theme } from '@theme';

interface NotificationContextType {
  toast: {
    success: (message: string) => void;
    error: (message: string) => void;
    info: (message: string) => void;
    warning: (message: string) => void;
    loading: (message: string) => string;
    dismiss: (toastId: string) => void;
  };
  alert: {
    success: (title: string, message?: string) => Promise<void>;
    error: (title: string, message?: string) => Promise<void>;
    warning: (title: string, message?: string) => Promise<void>;
    info: (title: string, message?: string) => Promise<void>;
    confirm: (title: string, message?: string, confirmText?: string, cancelText?: string) => Promise<boolean>;
  };
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Configuration SweetAlert2
  const swalConfig = {
    confirmButtonColor: theme.colors.primary,
    cancelButtonColor: theme.colors.error,
    customClass: {
      popup: 'swal-popup',
      title: 'swal-title',
      htmlContainer: 'swal-text',
      confirmButton: 'swal-confirm-button',
      cancelButton: 'swal-cancel-button',
    }
  };

  // Fonctions Toast
  const showToastSuccess = (message: string) => {
    toast.success(message, {
      duration: 3000,
      position: 'top-right',
      style: {
        background: theme.colors.success,
        color: 'white',
        padding: '16px',
        borderRadius: theme.borderRadius.md,
      },
      iconTheme: {
        primary: 'white',
        secondary: theme.colors.success,
      },
    });
  };

  const showToastError = (message: string) => {
    toast.error(message, {
      duration: 4000,
      position: 'top-right',
      style: {
        background: theme.colors.error,
        color: 'white',
        padding: '16px',
        borderRadius: theme.borderRadius.md,
      },
      iconTheme: {
        primary: 'white',
        secondary: theme.colors.error,
      },
    });
  };

  const showToastInfo = (message: string) => {
    toast(message, {
      duration: 3000,
      position: 'top-right',
      icon: 'ℹ️',
      style: {
        background: theme.colors.info,
        color: 'white',
        padding: '16px',
        borderRadius: theme.borderRadius.md,
      },
    });
  };

  const showToastWarning = (message: string) => {
    toast(message, {
      duration: 3500,
      position: 'top-right',
      icon: '⚠️',
      style: {
        background: theme.colors.warning,
        color: 'white',
        padding: '16px',
        borderRadius: theme.borderRadius.md,
      },
    });
  };

  const showToastLoading = (message: string): string => {
    return toast.loading(message, {
      position: 'top-right',
      style: {
        background: theme.colors.primary,
        color: 'white',
        padding: '16px',
        borderRadius: theme.borderRadius.md,
      },
    });
  };

  const dismissToast = (toastId: string) => {
    toast.dismiss(toastId);
  };

  // Fonctions SweetAlert
  const showAlertSuccess = async (title: string, message?: string) => {
    await Swal.fire({
      ...swalConfig,
      icon: 'success',
      title: title,
      text: message,
      timer: 3000,
      showConfirmButton: true,
    });
  };

  const showAlertError = async (title: string, message?: string) => {
    await Swal.fire({
      ...swalConfig,
      icon: 'error',
      title: title,
      text: message,
      showConfirmButton: true,
    });
  };

  const showAlertWarning = async (title: string, message?: string) => {
    await Swal.fire({
      ...swalConfig,
      icon: 'warning',
      title: title,
      text: message,
      showConfirmButton: true,
    });
  };

  const showAlertInfo = async (title: string, message?: string) => {
    await Swal.fire({
      ...swalConfig,
      icon: 'info',
      title: title,
      text: message,
      showConfirmButton: true,
    });
  };

  const showAlertConfirm = async (
    title: string,
    message?: string,
    confirmText: string = 'Oui',
    cancelText: string = 'Non'
  ): Promise<boolean> => {
    const result = await Swal.fire({
      ...swalConfig,
      icon: 'question',
      title: title,
      text: message,
      showCancelButton: true,
      confirmButtonText: confirmText,
      cancelButtonText: cancelText,
    });

    return result.isConfirmed;
  };

  const value: NotificationContextType = {
    toast: {
      success: showToastSuccess,
      error: showToastError,
      info: showToastInfo,
      warning: showToastWarning,
      loading: showToastLoading,
      dismiss: dismissToast,
    },
    alert: {
      success: showAlertSuccess,
      error: showAlertError,
      warning: showAlertWarning,
      info: showAlertInfo,
      confirm: showAlertConfirm,
    },
  };

  return (
    <NotificationContext.Provider value={value}>
      <Toaster />
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};
